import React, { useState, useEffect } from 'react';
import { Receipt, Plus, Package, Clock, X } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import './Debe.css';

const Debe = ({ onDebtComplete }) => {
    const [debtExpenses, setDebtExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [formData, setFormData] = useState({
        description: '',
        quantity: 1,
        unit_price: 0,
        notes: ''
    });

    useEffect(() => {
        loadDebtExpenses();
    }, []);

    const loadDebtExpenses = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/expenses/debt`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setDebtExpenses(data);
            }
        } catch (error) {
            console.error('Error loading debt expenses:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('es-CO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTotalDebt = () => {
        return debtExpenses.reduce((total, expense) => total + (expense.total || 0), 0);
    };

    const handleAddExpense = async (e) => {
        e.preventDefault();
        
        if (!formData.description.trim()) {
            alert('Por favor ingresa una descripción del gasto');
            return;
        }

        if (formData.unit_price <= 0) {
            alert('El precio unitario debe ser mayor a 0');
            return;
        }

        const total = formData.quantity * formData.unit_price;

        try {
            setLoading(true); // Activar loading mientras se guarda
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/expenses`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    description: formData.description,
                    quantity: formData.quantity,
                    unit_price: formData.unit_price,
                    notes: formData.notes,
                    total: total
                })
            });

            if (response.ok) {
                alert('Gasto registrado exitosamente');
                setShowAddModal(false);
                setFormData({
                    description: '',
                    quantity: 1,
                    unit_price: 0,
                    notes: ''
                });
                // Recargar la lista inmediatamente para mostrar el nuevo gasto
                await loadDebtExpenses();
                // Notificar al componente padre para recargar estadísticas
                if (onDebtComplete) {
                    onDebtComplete();
                }
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Error al registrar el gasto');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error registering expense:', error);
            alert('Error al conectar con el servidor');
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setFormData({
            description: '',
            quantity: 1,
            unit_price: 0,
            notes: ''
        });
    };

    return (
        <div className="debe-container">
            <div className="debe-header">
                <div>
                    <h2>Gestión de Deuda</h2>
                    <p className="debe-subtitle">Registra los gastos y deudas del día (almuerzos, cuchillas, etc.)</p>
                </div>
                <button className="add-debt-btn" onClick={() => setShowAddModal(true)}>
                    <Plus size={20} />
                    Agregar Gasto
                </button>
            </div>

            <div className="debt-summary-card">
                <div className="debt-summary-item">
                    <Receipt size={24} className="debt-icon" />
                    <div>
                        <p className="debt-summary-label">Total Deuda del Día</p>
                        <h3 className="debt-summary-value">{formatCurrency(getTotalDebt())}</h3>
                    </div>
                </div>
                <div className="debt-summary-item">
                    <Package size={24} className="debt-icon" />
                    <div>
                        <p className="debt-summary-label">Cantidad de Gastos</p>
                        <h3 className="debt-summary-value">{debtExpenses.length}</h3>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner" />
                </div>
            ) : debtExpenses.length === 0 ? (
                <div className="empty-state">
                    <Receipt size={48} />
                    <p>No hay gastos registrados hoy</p>
                    <button className="empty-state-btn" onClick={() => setShowAddModal(true)}>
                        <Plus size={18} />
                        Agregar Primer Gasto
                    </button>
                </div>
            ) : (
                <div className="debt-list">
                    <h3 className="debt-list-title">Lista de Gastos del Día</h3>
                    {debtExpenses.map((expense) => (
                        <div key={expense.id} className="debt-item-card">
                            <div className="debt-item-header">
                                <div className="debt-item-info">
                                    <Clock size={16} />
                                    <span className="debt-item-time">{formatDate(expense.created_at)}</span>
                                </div>
                                <div className="debt-item-total">
                                    {formatCurrency(expense.total)}
                                </div>
                            </div>
                            <div className="debt-item-items">
                                {expense.items && Array.isArray(expense.items) ? (
                                    expense.items.map((item, index) => (
                                        <div key={index} className="debt-item-detail">
                                            <span className="item-name">{item.name || 'Gasto'}</span>
                                            {item.quantity > 1 && (
                                                <span className="item-quantity">x {item.quantity}</span>
                                            )}
                                            <span className="item-price">
                                                {formatCurrency((item.price || 0) * (item.quantity || 1))}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-items">No hay detalles disponibles</p>
                                )}
                            </div>
                            {expense.notes && (
                                <div className="debt-item-notes">
                                    <strong>Notas:</strong> {expense.notes}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showAddModal && (
                <div className="debe-modal-overlay" onClick={handleCloseModal}>
                    <div className="debe-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="debe-modal-header">
                            <h2>Registrar Nuevo Gasto</h2>
                            <button onClick={handleCloseModal} className="close-modal-btn">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleAddExpense} className="debe-modal-content">
                            <div className="form-group">
                                <label>
                                    Descripción del Gasto *
                                    <span className="label-hint">(ej: Almuerzos, Cuchillas, Materiales, etc.)</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Ej: Almuerzos para el equipo"
                                    required
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Cantidad *</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Precio Unitario *</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formData.unit_price === 0 ? '' : formData.unit_price}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || value === null || value === undefined) {
                                                setFormData({ ...formData, unit_price: 0 });
                                            } else {
                                                const numValue = parseFloat(value);
                                                setFormData({ ...formData, unit_price: isNaN(numValue) ? 0 : numValue });
                                            }
                                        }}
                                        placeholder="0"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Total</label>
                                <input
                                    type="text"
                                    value={formatCurrency(formData.quantity * formData.unit_price)}
                                    readOnly
                                    className="total-input"
                                />
                            </div>
                            <div className="form-group">
                                <label>Notas (opcional)</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Notas adicionales sobre el gasto..."
                                    rows="3"
                                />
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={handleCloseModal} className="btn-cancel">
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit">
                                    Registrar Gasto
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Debe;
